import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { CardsApi } from '@shared/api/rest';
import { ApiUtils } from '@shared/utils';
import { type IQueryParams, RequestStrategyEnum, type ApiPaginate } from '@shared/api/models';
import type { CardResponse, CardsByAccountsRequestData, CardsRequest } from '@shared/api/rest/cards';

const getCardEffect = createEffect((params: IQueryParams<CardsRequest>) => CardsApi.getCards(params.request, params?.signal));
const getByAccountEffect = createEffect((params: IQueryParams<CardsByAccountsRequestData>) =>
  CardsApi.getCardsByAccounts(params.request, params?.signal),
);

const clearEvent = createEvent();

const $dataStore = createStore<ApiPaginate<CardResponse[]> | null>(null)
  .on(getCardEffect.done, (_, data) => data.result || null)
  .on(getByAccountEffect.done, (_, data) => data.result || null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardEffect.pending, (_, data) => data)
  .on(getCardEffect.fail, (_, data) => ApiUtils.abortApiFailCaseTrigger(data))
  .on(getCardEffect.done, () => false)
  .on(getByAccountEffect.pending, (_, data) => data)
  .on(getByAccountEffect.fail, () => false)
  .on(getByAccountEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardEffect, () => null)
  .on(getCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getCardEffect.done, () => null)
  .on(getByAccountEffect, () => null)
  .on(getByAccountEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getByAccountEffect.done, () => null)
  .reset(clearEvent);

$errorStore.watch((x) => {
  ApiUtils.errorStoreAbortRequestWatcher(x, $notificationStore.events.setError);
});

export const $cardListStore = {
  events: {
    update: ApiUtils.wrapperApi(getCardEffect, RequestStrategyEnum.Abort),
    updateByAccount: ApiUtils.wrapperApi(getByAccountEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  clear: clearEvent,
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
